import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes. Cancel anytime from your settings. No questions asked. Your access continues until the end of your billing period.',
  },
  {
    question: "What's included in the free plan?",
    answer: 'The free plan gives you 3 stories, basic session stats, and speed badges. You can try RSVP speed reading with no commitment. Upgrade anytime for unlimited stories, achievements, streaks, and analytics.',
  },
  {
    question: "What's the difference between Starter, Reader, and Ultra?",
    answer: '**Starter** ($4.49/mo) unlocks unlimited stories, progress saving, and basic analytics. **Reader** ($6.99/mo) adds achievements, streaks, full analytics, and priority support — it\'s the best value. **Ultra** ($9.99/mo) adds cloud sync across devices, history export, and streak freezes.',
  },
  {
    question: 'Can I upload my own books and documents?',
    answer: 'Yes. All paid plans let you upload .txt files so you can speed-read your own content. Reader and Ultra also support importing .epub files into the reader.',
  },
  {
    question: 'Will I lose my streak if I don\'t upgrade right away?',
    answer: 'No. Your current streak and all trial data are saved. When you upgrade later, everything is still there. But only paid plans let you keep building streaks with unlimited stories.',
  },
  {
    question: 'How long does it take to see results?',
    answer: 'Most users see a noticeable speed increase within their first 3 sessions — that\'s exactly what your trial showed. With daily practice on the full library, readers typically reach 400-700 WPM within a week.',
  },
  {
    question: 'Is my payment information secure?',
    answer: 'Yes. We use Stripe for payment processing — the same platform trusted by millions of businesses. We never store your card details on our servers.',
  },
];

export const UpsellFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="mb-16">
      <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-8">
        Frequently Asked Questions
      </h2>
      <div className="max-w-3xl mx-auto space-y-3">
        {faqData.map((item, index) => (
          <div key={index} className="faq-item">
            <button
              onClick={() => handleToggle(index)}
              className="w-full flex items-center justify-between p-5 text-left group"
              aria-expanded={openIndex === index}
            >
              <span className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {item.question}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ml-4 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? 'max-h-96' : 'max-h-0'
              }`}
            >
              <p className="px-5 pb-5 text-slate-600 dark:text-slate-400 leading-relaxed">
                {item.answer}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
